/**
 * Context-budget primitives - model context windows, compaction thresholds,
 * and the compact-summary message marker.
 *
 * Shared by the API (memory loading, compaction persistence) and the web
 * (context usage indicator, compact affordance) so the two cannot drift.
 * Generic by design: models resolve through a prefix registry with a default,
 * and compaction is a data-marked system chat_message - no bespoke tables.
 */
/** Fallback context window when a model is not in the registry. */
export const DEFAULT_MODEL_CONTEXT_WINDOW_TOKENS = 1_048_576;
/**
 * Known model context windows, matched by id prefix (first match wins, so list
 * more specific prefixes before broader ones). Extend with one entry per new
 * model family; unknown models fall back to
 * {@link DEFAULT_MODEL_CONTEXT_WINDOW_TOKENS} and {@link resolveModelContextWindow}
 * reports the miss so callers can log it instead of silently mis-sizing.
 *
 * These are hardcoded from provider docs rather than fetched live: window sizes
 * are effectively static per model and this only drives a UI usage indicator, so
 * a network round-trip per workspace load would not earn its latency.
 */
const MODEL_CONTEXT_WINDOWS = [
    { prefix: 'gemini-1.5-pro', tokens: 2_097_152 },
    { prefix: 'gemini-1.5-flash', tokens: 1_048_576 },
    { prefix: 'gemini-2.0', tokens: 1_048_576 },
    { prefix: 'gemini-2.5', tokens: 1_048_576 },
    // Gemini 3.x. Image-preview variants expose smaller input windows than the
    // text tiers, so list them before the broad gemini-3 entry (first match wins).
    { prefix: 'gemini-3.1-flash-image', tokens: 131_072 }, // 128k
    { prefix: 'gemini-3-pro-image', tokens: 65_536 }, // 65k
    // Text tiers - flash-lite, flash, pro-preview, 3.5-flash - all expose a 1M
    // input window (the models the dev/uat seeds configure).
    { prefix: 'gemini-3', tokens: 1_048_576 },
];
/**
 * Resolve a model id to its context window, reporting whether the registry
 * actually knew the model. Prefer this on the server so an unmatched model
 * (newly configured but not yet in the registry) can be logged rather than
 * silently sized to the default.
 */
export function resolveModelContextWindow(modelId) {
    if (!modelId)
        return { tokens: DEFAULT_MODEL_CONTEXT_WINDOW_TOKENS, matched: false };
    const normalized = modelId.toLowerCase().trim();
    const match = MODEL_CONTEXT_WINDOWS.find((entry) => normalized.startsWith(entry.prefix));
    return match
        ? { tokens: match.tokens, matched: true }
        : { tokens: DEFAULT_MODEL_CONTEXT_WINDOW_TOKENS, matched: false };
}
/** Resolve a model id to its context window size in tokens. */
export function getModelContextWindowTokens(modelId) {
    return resolveModelContextWindow(modelId).tokens;
}
/**
 * Context usage ratio (promptTokens / contextWindow) at which the UI starts
 * suggesting compaction (warning state on the usage indicator + Compact button).
 * Also the amber boundary of the usage donut.
 */
export const CONTEXT_COMPACT_SUGGEST_RATIO = 0.75;
/**
 * Below this ratio the context-usage donut is not rendered at all - a nearly
 * empty window isn't worth the visual noise. The donut appears once usage
 * crosses this floor.
 */
export const CONTEXT_DONUT_MIN_RATIO = 0.35;
/**
 * Red boundary of the usage donut. At/above this the window is close enough to
 * full that the indicator turns danger-colored.
 */
export const CONTEXT_DANGER_RATIO = 0.95;
/**
 * Context usage ratio at/above which the server auto-compacts BEFORE the next
 * turn so the conversation never overflows the model window. Measured from the
 * previous completed turn's context occupancy, so the practical effect is:
 * once a turn fills the window, compaction runs before the following turn.
 */
export const CONTEXT_AUTO_COMPACT_RATIO = 1.0;
/**
 * content_json.kind marking a `role='system'` chat_message row as a
 * conversation compact summary. Memory loading treats the latest such row as
 * the history boundary: the summary text replaces every message at or before
 * its messageIndex. The transcript never renders system rows.
 */
export const COMPACT_SUMMARY_CONTENT_KIND = 'compact_summary';
//# sourceMappingURL=ai-context.js.map